import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { NewsletterTextarea } from './NewsletterTextarea';
import { Mail, Rocket, Sparkles, CheckCircle, Send, Clock, ShieldCheck } from 'lucide-react';
import { EditableEmail } from './EditableEmail';
import { TrustBadgesCompact } from './TrustBadgesCompact';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { detectDeviceInfo } from '@/utils/deviceDetection';
import * as Sentry from '@sentry/react';
import { trackGTMEvent, trackGTMConversion, trackGTMSubscription, trackGTMConversionFunnel, trackGTMFormEngagement, trackGTMBeginCheckout, trackGTMPurchase } from './GoogleTagManager';
import { trackEvent as trackEventHj, trackConversionFunnelHj, trackFormEngagementHj } from '@/components/Hotjar';
import { trackEventMp, trackConversionFunnelMp, trackFormEngagementMp, trackSubscriptionMp } from '@/components/Mixpanel';
import { sentryUtils } from '@/utils/sentry';
import { useTranslation } from 'react-i18next';
import { useTrialDays } from '@/hooks/useTrialDays';
// useFeatureFlag removed - inline checkout is now always enabled
import { useABTest } from '@/hooks/useABTest';

const createSchemas = (t: any) => {
  const emailSchema = z.object({
    email: z.string().email(t('primaryCTA.errors.invalidEmail')),
  });

  const interestsSchema = z.object({
    interests: z
      .string()
      .min(30, t('primaryCTA.errors.interestsMin'))
      .max(500, t('primaryCTA.errors.interestsMax')),
  });

  return { emailSchema, interestsSchema };
};

type EmailData = {
  email: string;
};
type InterestsData = {
  interests: string;
};

interface PrimaryCTAProps {
  onSuccess: (email: string) => void;
  onDecommissioningOpen: () => void;
}

export const PrimaryCTA = ({ onSuccess, onDecommissioningOpen }: PrimaryCTAProps) => {
  const { t } = useTranslation();
  const { trialDays, isExtendedTrial } = useTrialDays();
  // Inline checkout always enabled (100% rollout)
  const { emailSchema, interestsSchema } = createSchemas(t);
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [isDeepLink, setIsDeepLink] = useState(false);
  const { toast } = useToast();
  
  // A/B Test: Primary CTA button copy
  const { variant: ctaVariant } = useABTest('primary_cta_button', ['A', 'B']);

  const emailForm = useForm<EmailData>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: '',
    },
  });

  const interestsForm = useForm<InterestsData>({
    resolver: zodResolver(interestsSchema),
    defaultValues: {
      interests: ''
    }
  });

  // Utility functions for deep linking
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 255;
  };

  const sanitizeInterests = (interests: string): string => {
    return interests.trim().substring(0, 500);
  };

  const checkRateLimit = (): boolean => {
    const lastDeepLinkTime = localStorage.getItem('last_deep_link');
    if (lastDeepLinkTime) {
      const timeDiff = Date.now() - parseInt(lastDeepLinkTime);
      if (timeDiff < 60000) { // 1 minute
        toast({
          title: t('primaryCTA.deepLink.tooManyRequests'),
          description: t('primaryCTA.deepLink.waitBeforeRetry'),
          variant: "destructive",
        });
        return false;
      }
    }
    return true;
  };

  // Process query strings for deep linking
  useEffect(() => {
    if (hasAutoOpened) return;

    const emailParam = searchParams.get('email');
    const interestsParam = searchParams.get('interests');

    if (!emailParam && !interestsParam) return;

    try {
      // Decode and sanitize parameters
      const decodedEmail = emailParam ? decodeURIComponent(emailParam).trim() : null;
      const decodedInterests = interestsParam ? sanitizeInterests(decodeURIComponent(interestsParam)) : null;

      // Case 1: Only email in URL
      if (decodedEmail && !decodedInterests) {
        if (isValidEmail(decodedEmail)) {
          emailForm.setValue('email', decodedEmail);
          
          setUserEmail(decodedEmail);
          setHasAutoOpened(true);
          setIsDeepLink(true);

          // Track email-only deep link
          trackGTMEvent('deep_link_used', { 
            email: decodedEmail,
            has_interests: false,
            source: 'primary_cta'
          });
          trackEventMp('deep_link_used', { 
            email: decodedEmail,
            has_interests: false,
            source: 'primary_cta'
          });
          trackGTMConversionFunnel('deep_link_modal_opened', 2, 3);
          trackConversionFunnelMp('deep_link_modal_opened', 2);

          // Sentry breadcrumb
          sentryUtils.trackNewsletterSubmission(decodedEmail, 'email');

          // Smooth scroll to modal
          setTimeout(() => {
            document.getElementById('primary-cta')?.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center' 
            });
          }, 300);
        }
      }

      // Case 2: Email + Interests (auto-open modal)
      if (decodedEmail && decodedInterests && isValidEmail(decodedEmail)) {
        // Set forms
        emailForm.setValue('email', decodedEmail);
        interestsForm.setValue('interests', decodedInterests);
        
        setUserEmail(decodedEmail);
        setHasAutoOpened(true);
        setIsDeepLink(true);

        // Track deep link usage
        trackGTMEvent('deep_link_used', { 
          email: decodedEmail,
          has_interests: true,
          source: 'primary_cta'
        });
        trackEventMp('deep_link_used', { 
          email: decodedEmail,
          has_interests: true,
          source: 'primary_cta'
        });
        trackGTMConversionFunnel('deep_link_modal_opened', 2, 3);
        trackConversionFunnelMp('deep_link_modal_opened', 2);

        // Sentry tracking
        Sentry.addBreadcrumb({
          category: 'deep-link',
          message: 'Auto-opened modal from query params',
          level: 'info',
          data: { email: decodedEmail, hasInterests: true }
        });

        // Smooth scroll to CTA
        setTimeout(() => {
          document.getElementById('primary-cta')?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }, 300);

        toast({
          title: t('primaryCTA.deepLink.autoFilled'),
          description: t('primaryCTA.deepLink.reviewAndSubmit'),
          variant: "default",
        });
      }

      // Case 3: Only interests (less common)
      if (!emailParam && decodedInterests) {
        interestsForm.setValue('interests', decodedInterests);
      }

    } catch (error) {
      console.error('Error processing deep link:', error);
      Sentry.captureException(error);
    }
  }, [searchParams, hasAutoOpened, emailForm, interestsForm, toast, t]);


  const handleEmailSubmit = async (data: EmailData) => {
    setIsSubmitting(true);
    
    try {
      // Track conversion funnel step and form engagement with GTM
      trackGTMBeginCheckout(data.email);
      trackGTMConversionFunnel('email_submitted', 1, 2);
      trackGTMFormEngagement('primary_cta', 'email_submit', 'email');
      trackGTMConversion('email_submitted', 2);
      trackConversionFunnelHj('email_submitted', 1);
      trackConversionFunnelMp('email_submitted', 1);
      trackFormEngagementHj('primary_cta', 'email_submit');
      trackFormEngagementMp('primary_cta', 'email_submit');
      sentryUtils.trackNewsletterSubmission(data.email, 'email');
      sentryUtils.trackConversionFunnel('email_submitted', 1, { email: data.email, source: 'primary_cta' });

      const email = data.email.trim();
      const deviceInfo = detectDeviceInfo();

      const params = new URLSearchParams(window.location.search);
      const payload = {
        email,
        browserName: deviceInfo.browserName,
        browserVersion: deviceInfo.browserVersion,
        deviceType: deviceInfo.deviceType,
        operatingSystem: deviceInfo.operatingSystem,
        userAgent: deviceInfo.userAgent,
        deviceLanguage: navigator.language,
        utm_source: params.get('utm_source') || 'primary_cta',
        utm_medium: params.get('utm_medium') || 'hero',
        utm_campaign: params.get('utm_campaign') || '',
        utm_term: params.get('utm_term') || undefined,
        utm_content: params.get('utm_content') || undefined,
        referrer_url: document.referrer || undefined,
        landing_page_url: window.location.href,
        checkout_flow_version: 'decommissioning',
        cta_copy_variant: ctaVariant, // A/B test variant
        capture_context: 'decommissioning',
      };

      // Capture prospect with primary CTA source using Sentry tracking
      const { data: result, error } = await sentryUtils.trackSupabaseFunction(
        'capture-prospect',
        () => supabase.functions.invoke('capture-prospect', { body: payload }),
        { email: data.email, deviceType: deviceInfo.deviceType, source: 'primary_cta' }
      );

      if (error) {
        sentryUtils.captureError(new Error('Prospect capture failed'), {
          email: data.email,
          error: error.message,
          deviceInfo,
          source: 'primary_cta'
        });
        throw error;
      }

      // Track prospect captured
      trackGTMEvent('prospect_captured', { user_email: email, source: 'primary_cta' });
      trackEventMp('prospect_captured', { user_email: email, source: 'primary_cta' });
      
      trackGTMEvent('decommissioning_lead_captured', { source: 'primary_cta' });
      trackEventMp('decommissioning_lead_captured', { source: 'primary_cta' });
      onDecommissioningOpen();
      setIsSubmitting(false);
      
    } catch (error) {
      console.error('Primary CTA submission error:', error);
      Sentry.captureException(error);
      
      toast({
        title: t('primaryCTA.errors.title'),
        description: t('primaryCTA.errors.generic'),
        variant: "destructive",
      });
      
      setIsSubmitting(false);
    }
  };

  const handleFinalSubmit = async (data: InterestsData) => {
    // Check rate limit for deep link submissions
    if (isDeepLink && !checkRateLimit()) {
      return;
    }

    // Store rate limit timestamp
    if (isDeepLink) {
      localStorage.setItem('last_deep_link', Date.now().toString());
    }

    try {
      // Track funnel completion with GTM (matching NewsletterForm events)
      trackGTMPurchase(userEmail, 'trial');
      trackGTMConversionFunnel('interests_submitted', 3, 10);
      trackGTMFormEngagement('primary_cta', 'interests_submit', 'interests');
      trackGTMSubscription(userEmail, 'trial');
      trackGTMConversion('newsletter_signup', 10, 'newsletter_conversion');
      trackConversionFunnelHj('interests_submitted', 3);
      trackConversionFunnelMp('interests_submitted', 3);
      trackFormEngagementHj('primary_cta', 'interests_submit');
      trackFormEngagementMp('primary_cta', 'interests_submit');
      sentryUtils.trackNewsletterSubmission(userEmail, 'interests');
      sentryUtils.trackConversionFunnel('interests_submitted', 3, { 
        email: userEmail, 
        source: isDeepLink ? 'deep_link' : 'primary_cta' 
      });

      // Detect device and browser information
      const deviceInfo = detectDeviceInfo();
      
      // Capture UTM parameters from URL
      const params = new URLSearchParams(window.location.search);
      
      // Prepare subscription data
      const subscriptionData = {
        interests: data.interests.trim(),
        email: userEmail.trim(),
        browserName: deviceInfo.browserName,
        browserVersion: deviceInfo.browserVersion,
        deviceType: deviceInfo.deviceType,
        operatingSystem: deviceInfo.operatingSystem,
        userAgent: deviceInfo.userAgent,
        referrer_url: document.referrer || undefined,
        landing_page_url: window.location.href,
        utm_source: params.get('utm_source') || undefined,
        utm_medium: params.get('utm_medium') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
        utm_term: params.get('utm_term') || undefined,
        utm_content: params.get('utm_content') || undefined
      };

      // Send data to Edge Function with Sentry tracking
      const { data: result, error } = await sentryUtils.trackSupabaseFunction(
        'capture-subscription',
        () => supabase.functions.invoke('capture-subscription', { body: subscriptionData }),
        { email: userEmail, interests: data.interests, source: 'primary_cta' }
      );

      if (error) {
        sentryUtils.captureError(new Error('Subscription capture failed'), {
          email: userEmail,
          error: error.message,
          subscriptionData,
          source: 'primary_cta'
        });

        // Handle duplicate email case
        if (error.message?.includes('EMAIL_EXISTS') || result?.error === 'EMAIL_EXISTS') {
          toast({
            title: t('primaryCTA.errors.emailExists'),
            description: t('primaryCTA.errors.emailExistsDescription'),
            variant: "default",
          });
          return;
        }
        
        toast({
          title: t('primaryCTA.errors.processingError'),
          description: t('primaryCTA.errors.processingErrorDescription'),
          variant: "destructive",
        });
        return;
      }

      // Track subscription completion in analytics platforms with GTM (matching NewsletterForm)
      trackGTMEvent('prospect_completed', { user_email: userEmail.trim(), source: 'primary_cta' });
      trackEventMp('prospect_completed', { user_email: userEmail.trim(), source: 'primary_cta' });
      trackGTMConversionFunnel('signup_completed', 4, 10);
      trackConversionFunnelHj('signup_completed', 4);
      trackConversionFunnelMp('signup_completed', 4);

      // Close modal and reset forms
      setShowModal(false);
      emailForm.reset();
      interestsForm.reset();
      
      // Now call onSuccess after interests are submitted
      onSuccess(userEmail.trim());
    } catch (error) {
      console.error('Primary CTA final submission error:', error);
      Sentry.captureException(error);
      toast({
        title: t('primaryCTA.errors.unexpectedError'),
        description: t('primaryCTA.errors.unexpectedErrorDescription'),
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <div className="max-w-xl sm:max-w-2xl lg:max-w-3xl mx-auto mb-8" id="primary-cta">
        <div className="px-4 sm:px-0">
          <Form {...emailForm}>
            <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 min-w-0">
              <FormField
                control={emailForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/80 w-4 h-4 z-10 pointer-events-none" />
                        <Input
                          {...field}
                          id="primary-email-input"
                          type="email"
                          placeholder={t('primaryCTA.emailPlaceholder')}
                          className="pl-10 h-14 text-base ios-transparent-input border border-white/30 hover:border-white/50 focus:border-newsletter-primary transition-all duration-300 shadow-2xl text-white placeholder:text-gray-300"
                          disabled={isSubmitting}
                           onFocus={() => {
                            trackGTMFormEngagement('primary_cta', 'email_focused', 'email');
                            trackFormEngagementHj('primary_cta', 'email_focused');
                            trackFormEngagementMp('primary_cta', 'email_focused');
                          }}
                          onBlur={() => {
                            trackGTMFormEngagement('primary_cta', 'email_blurred', 'email');
                            trackFormEngagementHj('primary_cta', 'email_blurred');
                            trackFormEngagementMp('primary_cta', 'email_blurred');
                          }}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button
              id="primary-cta-button"
              type="submit"
              disabled={isSubmitting}
              className="group relative overflow-hidden h-14 px-10 text-white border-2 border-white/20 hover:border-white/40 rounded-2xl font-semibold text-base transition-all hover:scale-105 hover:-translate-y-1 flex items-center justify-center gap-2 whitespace-nowrap animate-smooth-pulse"
              style={{
                background: 'linear-gradient(135deg, #FF6B35 0%, #FF8F65 50%, #FFB085 100%)',
                boxShadow: '0 8px 32px rgba(255, 107, 53, 0.3)',
                touchAction: 'manipulation'
              }}
            >
              {/* Gradient overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
              
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('primaryCTA.buttonProcessing')}
                </div>
              ) : (
                <>
                  <Rocket className="w-5 h-5 flex-shrink-0" />
                  <span>
                    {ctaVariant === 'A' 
                      ? t('primaryCTA.buttonVariantA') 
                      : t('primaryCTA.buttonVariantB')
                    }
                  </span>
                </>
              )}
            </Button>
          </form>
        </Form>

        {/* Trust Badges - Compact Visual Version */}
        <TrustBadgesCompact variant="light" />
        </div>
      </div>

      {/* Interests Modal */}
      <Dialog open={showModal} onOpenChange={(open) => {
        if (!open) {
          // Track modal close with GTM (differentiate from newsletter form)
          trackGTMEvent('primary_modal_closed', {
            event_category: 'form_interaction',
            event_label: 'modal_dismissed',
            source: 'primary_cta'
          });
          trackEventMp('primary_modal_closed', {
            event_category: 'form_interaction',
            event_label: 'modal_dismissed',
            source: 'primary_cta'
          });
          trackEventHj('primary_modal_closed');
          trackFormEngagementHj('primary_cta', 'modal_closed');
          trackFormEngagementMp('primary_cta', 'modal_closed');
          trackGTMConversionFunnel('modal_abandoned', 3);
          trackConversionFunnelHj('modal_abandoned', 3);
          trackConversionFunnelMp('modal_abandoned', 3);
        }
        setShowModal(open);
      }}>
        <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 bg-gradient-to-r from-newsletter-primary/5 to-newsletter-secondary/5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-newsletter-primary/20 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-newsletter-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-xl font-bold text-newsletter-primary">
                    {t('primaryCTA.modal.title')}
                  </DialogTitle>
                  {isDeepLink && (
                    <Badge variant="secondary" className="text-xs">
                      {t('primaryCTA.deepLink.prefilledBadge')}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('primaryCTA.modal.subtitle')}
                </p>
              </div>
            </div>
            
            {/* Progress indicator */}
            <div className="flex items-center gap-2 mt-4">
              <div className="flex-1 h-2 bg-newsletter-primary rounded-full" />
              <div className="flex-1 h-2 bg-newsletter-primary rounded-full" />
              <span className="text-xs text-newsletter-primary font-medium ml-2">2/2</span>
            </div>
          </DialogHeader>
          
          <div className="p-6 pt-2">
            {/* Show email confirmation */}
            <EditableEmail 
              email={userEmail}
              onEmailChange={(newEmail) => {
                setUserEmail(newEmail);
                emailForm.setValue('email', newEmail);
              }}
              source="primary_cta"
            />

            <Form {...interestsForm}>
              <form onSubmit={interestsForm.handleSubmit(handleFinalSubmit)} className="space-y-4">
                <FormField
                  control={interestsForm.control}
                  name="interests"
                  render={({ field, fieldState }) => (
                    <FormItem className="space-y-4">
                      <div className="flex items-center gap-2 text-newsletter-primary">
                        <Sparkles className="w-5 h-5" />
                        <span className="text-sm font-medium">{t('primaryCTA.modal.interestsTitle')}</span>
                      </div>
                      <FormControl>
                        <NewsletterTextarea 
                          value={field.value} 
                          onChange={field.onChange}
                          error={fieldState.error?.message}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <Button
                  id="finalize-subscription-button"
                  type="submit"
                  disabled={interestsForm.formState.isSubmitting}
                  variant="premium"
                  className="
                    w-full h-12 text-base font-semibold rounded-xl
                    transition-all ease-out
                    disabled:opacity-50
                    shadow-newsletter
                    group
                  "
                >
                  {interestsForm.formState.isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                      {t('primaryCTA.modal.finalizeProcessing')}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                      {t('primaryCTA.modal.finalizeButton')}
                      <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                    </div>
                  )}
                </Button>
              </form>
            </Form>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};